import {
  Component,
  inject,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
  HostListener,
  untracked,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OnboardingWelcomeService } from '../../shared/services/onboarding-welcome.service';
import { OnboardingService } from '../../shared/services/onboarding.service';
import { AuthService } from '../../services/auth';

/**
 * Intro video shown (collapsed) in the welcome guide.
 * Set to a YouTube embed URL (https://www.youtube.com/embed/<id>) to enable it;
 * leave null to hide the video row entirely.
 */
export const WELCOME_VIDEO_URL: string | null = null;

type WelcomeStep = {
  key: 'hasProfessionals' | 'hasServices' | 'hasSchedule';
  title: string;
  hint: string;
  route: string;
};

@Component({
  selector: 'app-welcome-modal',
  standalone: true,
  templateUrl: './welcome-modal.html',
  styleUrl: './welcome-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WelcomeModal {
  private welcomeService = inject(OnboardingWelcomeService);
  private onboardingService = inject(OnboardingService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);

  isVisible = this.welcomeService.showWelcome;
  /** Section the guide sent the user to; progress is refreshed when they leave it */
  private visitedStepRoute: string | null = null;
  userName = signal('');
  videoOpen = signal(false);

  readonly steps: WelcomeStep[] = [
    {
      key: 'hasProfessionals',
      title: 'Agrega a tu equipo',
      hint: 'Quiénes atienden y en qué agenda.',
      route: '/settings/professionals',
    },
    {
      key: 'hasServices',
      title: 'Crea tus servicios y precios',
      hint: 'Lo que tus clientes podrán reservar.',
      route: '/settings/services',
    },
    {
      key: 'hasSchedule',
      title: 'Define tu horario de atención',
      hint: 'Los días y horas en que recibes citas.',
      route: '/settings/business-hours',
    },
  ];

  /** Session-only opt-out for the floating reopen pill */
  private static readonly PILL_HIDDEN_KEY = 'citalink.welcomePillHidden';
  pillHidden = signal(WelcomeModal.readPillHidden());

  /**
   * Floating "Guía de inicio" pill: keeps the pending steps one click away
   * after the panel is closed (e.g. after jumping to one of the steps).
   */
  showPill = computed(
    () =>
      !this.isVisible() &&
      !this.pillHidden() &&
      this.onboardingService.shouldShowChecklist() &&
      this.authService.isOwner() &&
      !this.authService.isSystemAdmin()
  );

  status = this.onboardingService.status;
  completedSteps = this.onboardingService.completedSteps;
  isComplete = this.onboardingService.isComplete;
  totalSteps = this.onboardingService.totalSteps;

  /** First pending step gets the primary emphasis */
  nextStepKey = computed(() => this.steps.find(s => !this.status()[s.key])?.key ?? null);

  videoUrl: SafeResourceUrl | null = WELCOME_VIDEO_URL
    ? this.sanitizer.bypassSecurityTrustResourceUrl(WELCOME_VIDEO_URL)
    : null;

  constructor() {
    // Refresh name and checklist every time the guide opens
    effect(() => {
      if (!this.isVisible()) return;
      untracked(() => {
        this.userName.set(this.authService.getUserInfo()?.given_name || '');
        this.videoOpen.set(false);
        this.onboardingService.checkPrerequisites(true);
      });
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(e => {
        if (this.visitedStepRoute && !e.urlAfterRedirects.startsWith(this.visitedStepRoute)) {
          this.visitedStepRoute = null;
          this.onboardingService.checkPrerequisites(true);
        }
      });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isVisible()) this.close();
  }

  close(): void {
    this.welcomeService.dismiss();
  }

  goTo(route: string): void {
    this.visitedStepRoute = route;
    this.welcomeService.dismiss();
    this.router.navigateByUrl(route);
  }

  open(): void {
    this.welcomeService.open();
  }

  hidePill(): void {
    this.pillHidden.set(true);
    try {
      sessionStorage.setItem(WelcomeModal.PILL_HIDDEN_KEY, '1');
    } catch {
      // Storage unavailable: hidden for this page view only
    }
  }

  private static readPillHidden(): boolean {
    try {
      return sessionStorage.getItem(WelcomeModal.PILL_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  }

  toggleVideo(): void {
    this.videoOpen.update(v => !v);
  }
}
